import React, { useState, useEffect } from 'react';
import { AlertOctagon, Radio, XCircle, Activity, Users, ArrowRight, Eye, Map, Shield, Clock, AlertTriangle, Lock, MapPin, CheckCircle } from 'lucide-react';
import Security from './Security';

interface EmergencyData {
  timestamp: string;
  buildingId: string;
  totalOccupants: number;
  locationBreakdown: {
    [key: string]: {
      occupants: number;
      activePaths: number;
      nearestExit: string;
      status: 'clear' | 'occupied' | 'threat-detected' | 'unknown';
      lastMovement: string;
    };
  };
  activeThreats: {
    id: string;
    location: string;
    type: string;
    lastSeen: string;
    movementPattern: string[];
    riskLevel: 'high' | 'medium' | 'low';
  }[];
  evacuationRoutes: {
    route: string;
    status: 'clear' | 'blocked' | 'congested';
    capacity: number;
    currentFlow: number;
  }[];
  securedAreas: {
    area: string;
    status: 'locked' | 'unlocked' | 'compromised';
    lastAccess: string;
  }[];
}

const AdvancedSecurity: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'live' | 'after-hours'>('live');
  const [isEmergencyMode, setIsEmergencyMode] = useState(false);
  const [lastReport, setLastReport] = useState<string | null>(null);
  const [showEmergencyPortal, setShowEmergencyPortal] = useState(false);
  const [emergencyData, setEmergencyData] = useState<EmergencyData>({
    timestamp: new Date().toISOString(),
    buildingId: "HQ-001",
    totalOccupants: 342,
    locationBreakdown: {
      "Floor 1 - North Wing": {
        occupants: 45,
        activePaths: 12,
        nearestExit: "NE1",
        status: "occupied",
        lastMovement: "30 seconds ago"
      },
      "Floor 1 - South Wing": {
        occupants: 38,
        activePaths: 8,
        nearestExit: "SE1",
        status: "clear",
        lastMovement: "2 minutes ago"
      },
      "Floor 2 - Conference Center": {
        occupants: 85,
        activePaths: 25,
        nearestExit: "NE2",
        status: "threat-detected",
        lastMovement: "5 seconds ago"
      },
      "Floor 2 - Open Office": {
        occupants: 120,
        activePaths: 40,
        nearestExit: "SE2",
        status: "occupied",
        lastMovement: "15 seconds ago"
      },
      "Floor 3 - Executive Wing": {
        occupants: 54,
        activePaths: 15,
        nearestExit: "NE3",
        status: "occupied",
        lastMovement: "45 seconds ago"
      }
    },
    activeThreats: [
      {
        id: "THR-001",
        location: "Floor 2 - Conference Center",
        type: "Unauthorized Movement",
        lastSeen: "5 seconds ago",
        movementPattern: ["F2-C12", "F2-C15", "F2-C18", "F2-Corridor-E"],
        riskLevel: "high"
      }
    ],
    evacuationRoutes: [
      {
        route: "North Emergency Stairwell",
        status: "clear",
        capacity: 150,
        currentFlow: 45
      },
      {
        route: "South Emergency Stairwell",
        status: "congested",
        capacity: 150,
        currentFlow: 120
      },
      {
        route: "Main Entrance",
        status: "blocked",
        capacity: 200,
        currentFlow: 0
      }
    ],
    securedAreas: [
      {
        area: "Server Room",
        status: "locked",
        lastAccess: "15 minutes ago"
      },
      {
        area: "Executive Offices",
        status: "locked",
        lastAccess: "2 minutes ago"
      },
      {
        area: "Research Lab",
        status: "compromised",
        lastAccess: "1 minute ago"
      }
    ]
  });

  useEffect(() => {
    if (isEmergencyMode) {
      const interval = setInterval(() => {
        setEmergencyData(prev => ({
          ...prev,
          timestamp: new Date().toISOString(),
          locationBreakdown: {
            ...prev.locationBreakdown,
            "Floor 2 - Conference Center": {
              ...prev.locationBreakdown["Floor 2 - Conference Center"],
              lastMovement: "Just now",
              activePaths: prev.locationBreakdown["Floor 2 - Conference Center"].activePaths + 
                (Math.random() > 0.5 ? 1 : -1)
            }
          },
          activeThreats: [
            {
              ...prev.activeThreats[0],
              lastSeen: "Just now",
              movementPattern: [...prev.activeThreats[0].movementPattern, `F2-C${Math.floor(Math.random() * 20)}`].slice(-5)
            }
          ]
        }));
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isEmergencyMode]);

  const handleEmergencyReport = () => {
    setIsEmergencyMode(true);
    setLastReport(new Date().toISOString());
  };

  const handleStopEmergency = () => {
    setIsEmergencyMode(false);
    setLastReport(null);
    setShowEmergencyPortal(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'clear': return '#0A9B9B';
      case 'occupied': return '#8a7355';
      case 'threat-detected': return '#c33';
      case 'blocked': return '#c33';
      case 'congested': return '#8a7355';
      case 'compromised': return '#c33';
      case 'locked': return '#0A9B9B';
      default: return '#666';
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case 'clear': return 'var(--sca-badge-bg)';
      case 'occupied': return '#fef9e7';
      case 'threat-detected': return '#fee';
      case 'blocked': return '#fee';
      case 'congested': return '#fef9e7';
      case 'compromised': return '#fee';
      case 'locked': return 'var(--sca-badge-bg)';
      default: return 'var(--sca-hover)';
    }
  };

  const securityData = {
    currentAlerts: [
      {
        id: 1,
        type: 'Unusual Movement',
        location: 'Server Room Corridor',
        timestamp: '10:45 AM',
        severity: 'high',
        description: 'Repeated access attempts detected'
      },
      {
        id: 2,
        type: 'Tailgating',
        location: 'Main Entrance',
        timestamp: '10:30 AM',
        severity: 'medium',
        description: 'Multiple entries on single access'
      },
      {
        id: 3,
        type: 'After Hours',
        location: 'Finance Department',
        timestamp: '10:15 AM',
        severity: 'low',
        description: 'Unexpected activity detected'
      }
    ],
    restrictedAreas: [
      {
        name: 'Server Room',
        status: 'Secured',
        authorizedCount: 2,
        lastAccess: '10:30 AM'
      },
      {
        name: 'Executive Suite',
        status: 'Secured',
        authorizedCount: 1,
        lastAccess: '09:45 AM'
      },
      {
        name: 'R&D Lab',
        status: 'Secured',
        authorizedCount: 4,
        lastAccess: '10:15 AM'
      }
    ],
    movementAnalysis: {
      unusualPatterns: 2,
      activeMonitoring: 15,
      securedZones: 8,
      currentOccupancy: {
        restricted: 3,
        authorized: 289,
        visitors: 53
      }
    },
    recentEvents: [
      {
        time: '10:45 AM',
        event: 'Access Granted - Server Room',
        user: 'John Smith',
        type: 'authorized'
      },
      {
        time: '10:30 AM',
        event: 'Multiple Entry Attempt - Main Entrance',
        user: 'Unknown',
        type: 'warning'
      },
      {
        time: '10:15 AM',
        event: 'Access Denied - Executive Suite',
        user: 'Jane Doe',
        type: 'unauthorized'
      },
      {
        time: '10:00 AM',
        event: 'Unusual Movement Pattern',
        user: 'System Alert',
        type: 'warning'
      }
    ]
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'high':
        return { bg: '#fee', color: '#c33' };
      case 'medium':
        return { bg: '#fef9e7', color: '#8a7355' };
      case 'low':
        return { bg: 'var(--sca-badge-bg)', color: 'var(--sca-teal)' };
      default:
        return { bg: 'var(--sca-hover)', color: '#666' };
    }
  };

  const getEventTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'warning':
        return '#8a7355';
      case 'unauthorized':
        return '#c33';
      case 'authorized':
        return '#0A9B9B';
      default:
        return '#666';
    }
  };

  return (
    <div className="p-6 space-y-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Security Operations
        </h2>
        <p style={{ color: 'var(--sca-muted)' }}>
          Live security monitoring and emergency response. Sample building data, illustrative
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b" style={{ borderColor: 'var(--sca-border)' }}>
        <button
          onClick={() => setActiveTab('live')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'live' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'live' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Live Operations
        </button>
        <button
          onClick={() => setActiveTab('after-hours')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'after-hours' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'after-hours' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          After-Hours Review
        </button>
      </div>

      {/* Live Operations Tab */}
      {activeTab === 'live' && (
        <div className="space-y-6">
          {/* Emergency Controls */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <AlertOctagon size={20} style={{ color: isEmergencyMode ? '#c33' : 'var(--sca-muted)', marginRight: 8 }} />
                <h3 className="text-lg font-semibold" style={{ color: 'var(--sca-ink)' }}>Emergency Response Status</h3>
              </div>
              {isEmergencyMode ? (
                <div className="flex items-center gap-2">
                  <Radio size={18} style={{ color: '#c33' }} className="animate-pulse" />
                  <span className="font-medium" style={{ color: '#c33' }}>LIVE DATA STREAMING</span>
                  <button
                    onClick={() => setShowEmergencyPortal(!showEmergencyPortal)}
                    className="ml-4 px-3 py-1.5 text-sm rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--sca-teal)',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Eye size={14} />
                      {showEmergencyPortal ? 'Hide' : 'View'} Emergency Services Portal
                    </div>
                  </button>
                </div>
              ) : (
                <span style={{ color: 'var(--sca-muted)' }}>Standing By</span>
              )}
            </div>

            <div className="flex space-x-4">
              {!isEmergencyMode ? (
                <button
                  onClick={handleEmergencyReport}
                  className="flex-1 py-3 px-4 rounded-lg font-medium transition-colors"
                  style={{
                    backgroundColor: '#c33',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#a22'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#c33'}
                >
                  <div className="flex items-center justify-center gap-2">
                    <AlertTriangle size={18} />
                    Send Emergency Report
                  </div>
                </button>
              ) : (
                <button
                  onClick={handleStopEmergency}
                  className="flex-1 py-3 px-4 rounded-lg font-medium transition-colors"
                  style={{
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#555'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#666'}
                >
                  <div className="flex items-center justify-center gap-2">
                    <XCircle size={18} />
                    Stop Emergency Report
                  </div>
                </button>
              )}
            </div>

            {isEmergencyMode && (
              <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: '#fee', border: '1px solid #fcc' }}>
                <p className="font-medium" style={{ color: '#c33' }}>Emergency services have been notified and are receiving live data.</p>
                <p className="text-sm mt-1" style={{ color: '#c33' }}>Last report sent: {new Date(lastReport!).toLocaleTimeString()}</p>
              </div>
            )}
          </div>

          {/* Emergency Services Portal View */}
          {showEmergencyPortal && (
            <div className="rounded-lg border p-6" style={{ backgroundColor: '#1a1a1a', borderColor: '#333' }}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Shield size={20} style={{ color: '#c33' }} />
                  <h3 className="text-xl font-bold" style={{ color: 'white' }}>Emergency Services Portal</h3>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} style={{ color: '#999' }} />
                  <span style={{ color: '#999' }}>Last Update: {new Date(emergencyData.timestamp).toLocaleTimeString()}</span>
                </div>
              </div>

              {/* Building Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="rounded-lg p-4" style={{ backgroundColor: '#2a2a2a' }}>
                  <h4 className="mb-2" style={{ color: '#999' }}>Total Occupants</h4>
                  <div className="flex items-center gap-2">
                    <Users size={18} style={{ color: 'var(--sca-teal)' }} />
                    <span className="text-2xl font-bold" style={{ color: 'white' }}>{emergencyData.totalOccupants}</span>
                  </div>
                </div>
                <div className="rounded-lg p-4" style={{ backgroundColor: '#2a2a2a' }}>
                  <h4 className="mb-2" style={{ color: '#999' }}>Active Threats</h4>
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={18} style={{ color: '#c33' }} />
                    <span className="text-2xl font-bold" style={{ color: 'white' }}>{emergencyData.activeThreats.length}</span>
                  </div>
                </div>
                <div className="rounded-lg p-4" style={{ backgroundColor: '#2a2a2a' }}>
                  <h4 className="mb-2" style={{ color: '#999' }}>Building ID</h4>
                  <div className="flex items-center gap-2">
                    <Map size={18} style={{ color: '#999' }} />
                    <span className="text-2xl font-bold" style={{ color: 'white' }}>{emergencyData.buildingId}</span>
                  </div>
                </div>
              </div>

              {/* Location Breakdown */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4" style={{ color: 'white' }}>Location Status</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(emergencyData.locationBreakdown).map(([location, data]) => (
                    <div key={location} className="rounded-lg p-4" style={{ backgroundColor: '#2a2a2a' }}>
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-medium" style={{ color: 'white' }}>{location}</h5>
                        <span className="text-xs font-medium px-2 py-1 rounded" style={{
                          backgroundColor: getStatusBg(data.status),
                          color: getStatusColor(data.status)
                        }}>
                          {data.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span style={{ color: '#999' }}>Occupants:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{data.occupants}</span>
                        </div>
                        <div>
                          <span style={{ color: '#999' }}>Active Paths:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{data.activePaths}</span>
                        </div>
                        <div>
                          <span style={{ color: '#999' }}>Nearest Exit:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{data.nearestExit}</span>
                        </div>
                        <div>
                          <span style={{ color: '#999' }}>Last Movement:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{data.lastMovement}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Active Threats */}
              {emergencyData.activeThreats.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold mb-4" style={{ color: 'white' }}>Active Threats</h4>
                  {emergencyData.activeThreats.map(threat => (
                    <div key={threat.id} className="rounded-lg p-4" style={{ backgroundColor: '#3a1a1a' }}>
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-medium" style={{ color: '#c33' }}>{threat.type}</h5>
                          <p className="text-sm" style={{ color: '#999' }}>ID: {threat.id}</p>
                        </div>
                        <span className="text-sm font-medium px-2 py-1 rounded" style={{
                          backgroundColor: '#fee',
                          color: '#c33'
                        }}>
                          {threat.riskLevel.toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                        <div>
                          <span style={{ color: '#999' }}>Location:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{threat.location}</span>
                        </div>
                        <div>
                          <span style={{ color: '#999' }}>Last Seen:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{threat.lastSeen}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm" style={{ color: '#999' }}>Movement Pattern:</span>
                        <div className="flex items-center mt-1 flex-wrap gap-1">
                          {threat.movementPattern.map((point, index) => (
                            <React.Fragment key={index}>
                              <span className="text-sm font-medium" style={{ color: 'white' }}>{point}</span>
                              {index < threat.movementPattern.length - 1 && (
                                <ArrowRight size={12} style={{ color: '#999' }} />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Evacuation Routes */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4" style={{ color: 'white' }}>Evacuation Routes</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {emergencyData.evacuationRoutes.map(route => (
                    <div key={route.route} className="rounded-lg p-4" style={{ backgroundColor: '#2a2a2a' }}>
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-medium" style={{ color: 'white' }}>{route.route}</h5>
                        <span className="text-xs font-medium px-2 py-1 rounded" style={{
                          backgroundColor: getStatusBg(route.status),
                          color: getStatusColor(route.status)
                        }}>
                          {route.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span style={{ color: '#999' }}>Capacity:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{route.capacity}</span>
                        </div>
                        <div>
                          <span style={{ color: '#999' }}>Current Flow:</span>
                          <span className="ml-2 font-medium" style={{ color: 'white' }}>{route.currentFlow}</span>
                        </div>
                        <div className="w-full rounded-full h-2" style={{ backgroundColor: '#444' }}>
                          <div
                            className="h-2 rounded-full"
                            style={{
                              width: `${(route.currentFlow / route.capacity) * 100}%`,
                              backgroundColor: getStatusColor(route.status)
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Secured Areas */}
              <div>
                <h4 className="text-lg font-semibold mb-4" style={{ color: 'white' }}>Secured Areas</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {emergencyData.securedAreas.map(area => (
                    <div key={area.area} className="rounded-lg p-4" style={{ backgroundColor: '#2a2a2a' }}>
                      <div className="flex justify-between items-start mb-2">
                        <h5 className="font-medium" style={{ color: 'white' }}>{area.area}</h5>
                        <span className="text-xs font-medium px-2 py-1 rounded" style={{
                          backgroundColor: getStatusBg(area.status),
                          color: getStatusColor(area.status)
                        }}>
                          {area.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <span style={{ color: '#999' }}>Last Access:</span>
                        <span className="ml-2 font-medium" style={{ color: 'white' }}>{area.lastAccess}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Regular Security Monitoring */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center mb-2 gap-2">
                <AlertTriangle size={18} style={{ color: '#c33' }} />
                <h3 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>Active Alerts</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{securityData.currentAlerts.length}</p>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Security incidents</p>
            </div>

            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center mb-2 gap-2">
                <Map size={18} style={{ color: 'var(--sca-teal)' }} />
                <h3 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>Monitored Zones</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{securityData.movementAnalysis.activeMonitoring}</p>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Active tracking</p>
            </div>

            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center mb-2 gap-2">
                <Users size={18} style={{ color: 'var(--sca-teal)' }} />
                <h3 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>Current Occupancy</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>
                {securityData.movementAnalysis.currentOccupancy.authorized + 
                 securityData.movementAnalysis.currentOccupancy.visitors}
              </p>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>People in building</p>
            </div>

            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center mb-2 gap-2">
                <Lock size={18} style={{ color: 'var(--sca-teal)' }} />
                <h3 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>Restricted Areas</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{securityData.restrictedAreas.length}</p>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Secured zones</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Current Alerts</h3>
              <div className="space-y-4">
                {securityData.currentAlerts.map((alert) => {
                  const severity = getSeverityColor(alert.severity);
                  return (
                    <div key={alert.id} className="border-b pb-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="text-xs px-2 py-1 rounded font-medium" style={{
                            backgroundColor: severity.bg,
                            color: severity.color
                          }}>
                            {alert.severity.toUpperCase()}
                          </span>
                          <h4 className="font-medium mt-2" style={{ color: 'var(--sca-ink)' }}>{alert.type}</h4>
                        </div>
                        <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>{alert.timestamp}</span>
                      </div>
                      <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>{alert.location}</p>
                      <p className="text-sm mt-1" style={{ color: 'var(--sca-muted)' }}>{alert.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Restricted Area Status</h3>
              <div className="space-y-4">
                {securityData.restrictedAreas.map((area, index) => (
                  <div key={index} className="flex items-center justify-between border-b pb-4 last:border-b-0">
                    <div>
                      <p className="font-medium" style={{ color: 'var(--sca-ink)' }}>{area.name}</p>
                      <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                        Last access: {area.lastAccess}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium" style={{ color: 'var(--sca-teal)' }}>{area.status}</p>
                      <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>{area.authorizedCount} authorized</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Recent Security Events</h3>
              <div className="space-y-4">
                {securityData.recentEvents.map((event, index) => (
                  <div key={index} className="flex items-start justify-between border-b pb-4 last:border-b-0">
                    <div>
                      <p className="font-medium" style={{ color: getEventTypeColor(event.type) }}>
                        {event.event}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                        {event.time} • {event.user}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Movement Analysis</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span style={{ color: 'var(--sca-ink)' }}>Unusual Patterns Detected:</span>
                  <span className="font-medium" style={{ color: '#8a7355' }}>{securityData.movementAnalysis.unusualPatterns}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: 'var(--sca-ink)' }}>Active Monitoring Zones:</span>
                  <span className="font-medium" style={{ color: 'var(--sca-teal)' }}>{securityData.movementAnalysis.activeMonitoring}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: 'var(--sca-ink)' }}>Secured Zones:</span>
                  <span className="font-medium" style={{ color: 'var(--sca-teal)' }}>{securityData.movementAnalysis.securedZones}</span>
                </div>
                <div className="border-t pt-4 mt-4" style={{ borderColor: 'var(--sca-border)' }}>
                  <h4 className="font-medium mb-2" style={{ color: 'var(--sca-ink)' }}>Current Occupancy Breakdown</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>Restricted Areas:</span>
                      <span className="font-medium" style={{ color: '#c33' }}>{securityData.movementAnalysis.currentOccupancy.restricted}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>Authorized Personnel:</span>
                      <span className="font-medium" style={{ color: 'var(--sca-teal)' }}>{securityData.movementAnalysis.currentOccupancy.authorized}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm" style={{ color: 'var(--sca-ink)' }}>Visitors:</span>
                      <span className="font-medium" style={{ color: 'var(--sca-teal)' }}>{securityData.movementAnalysis.currentOccupancy.visitors}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* After-Hours Review Tab */}
      {activeTab === 'after-hours' && (
        <Security hideHeader={true} />
      )}
    </div>
  );
};

export default AdvancedSecurity;
